import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import Styles from './vm-dropdown-item.scss';

/**
 * A single item within a vm-dropdown menu.
 * Can contain nested vm-dropdown-item elements to form a group.
 * A divider is automatically shown above every item except the first sibling.
 *
 * @slot - Default slot for the item label text.
 * @slot icon-leading - Slot for a leading icon (before the label).
 * @slot icon-trailing - Slot for a trailing icon (after the label).
 * @slot children - Slot for nested child items (group items only).
 *
 * @fires vm-dropdown-item-click - Dispatched when a leaf item (no children) is clicked.
 *
 * @example
 * ```html
 * <!-- Leaf item -->
 * <vm-dropdown-item>
 *   <vm-icon-next slot="icon-leading" name="arrow-narrow-right"></vm-icon-next>
 *   Create new playlist
 * </vm-dropdown-item>
 *
 * <!-- Group item with children -->
 * <vm-dropdown-item group>
 *   Save to
 *   <vm-dropdown-item groupitem slot="children">LinkedIn</vm-dropdown-item>
 *   <vm-dropdown-item groupitem slot="children">Youtube</vm-dropdown-item>
 * </vm-dropdown-item>
 * ```
 */
@customElement('vm-dropdown-item')
export class VmDropdownItem extends LitElement {
    static override styles = Styles;

    /** Whether this item is a group header with nested children. */
    @property({ type: Boolean, reflect: true })
    group: boolean = false;

    /** Whether this item is a child within a group. */
    @property({ type: Boolean, reflect: true })
    groupitem: boolean = false;

    // Note: role and tabindex are set once on connect. The `group` property
    // is expected to be set declaratively in HTML and not toggled at runtime.
    override connectedCallback() {
        super.connectedCallback();
        this.classList.add('vm-dropdown-item');
        this.dataset.testid = 'vm-dropdown-item';
        this.setAttribute('role', this.group ? 'group' : 'menuitem');
        if (!this.group) {
            this.setAttribute('tabindex', '0');
        }
        this.addEventListener('click', this._onClick);
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this._onClick);
    }

    /** Dispatches a vm-dropdown-item-click custom event. */
    private _onClick = () => {
        if (this.group) return;

        this.dispatchEvent(
            new CustomEvent('vm-dropdown-item-click', {
                bubbles: true,
                composed: true,
            }),
        );
    };

    override render() {
        return html`
            <div class="vm-dropdown-item__label-wrap" data-testid="vm-dropdown-item-label-wrap">
                <slot name="icon-leading"></slot>
                <span class="vm-dropdown-item__label">
                    <slot></slot>
                </span>
                <slot name="icon-trailing"></slot>
            </div>
            <slot name="children"></slot>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-dropdown-item': VmDropdownItem;
    }
}
